import React from 'react'
import { NextPage } from 'next'
import dynamic from 'next/dynamic'
import { Box, useBreakpointValue } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { wrapper } from 'src/redux'
import { getSellPagesData } from 'src/utils/getStaticProps'
import { Head, LinkRel, MetaTags } from 'src/components/common'
import { useSelector } from 'react-redux'
import { useBackButton } from 'src/utils/hooks'
import { PAGE_CACHE_CONTROL } from 'server/constants'
import {getDocument} from "ssr-window";


const MobileActionBar = dynamic(() => import('src/components/SellOffers/MobileActionBar'))
const ActionBar = dynamic(() => import('src/components/SellOffers/ActionBar'))
const SellOffersContainer = dynamic(() => import('src/containers/SellOffersContainer'))
const SellMoreInfoContainer = dynamic(() => import('src/containers/Sections/NltMoreInfoContainer'))
const PageHeaderContainer = dynamic(() => import('src/containers/PageHeaderContainer'))

interface Props {
  page: any,
  offers: any,
  filteredOffers: any,
}

const SellSearchPage: NextPage<Props> = ({ page }) => {
  const { t } = useTranslation()
  const visibleOnMobile = useBreakpointValue({ base: true, md: false })
  const requestedPage = useSelector((state: Store.RootState) => state.sell.listingPage)

  useBackButton()

  const crumbs = [
    {
      page: 'Home Page',
      href: '/',
      title: 'Home Page',
    },
    {
      page: 'Auto in vendita',
      href: '/offerte-auto',
      title: 'Auto in vendita',
    },
  ]

  const metaPage = Math.max(page || 0, requestedPage || 0, 1)

  const resultFormatter = (({ name, code }) => {
    let destinationUrl = new URL(code);
    let currentDocumentUrl = new URLSearchParams(getDocument().location.search);
    let ref = currentDocumentUrl.get("ref");
    if (ref) {
      let {searchParams} = destinationUrl;
      searchParams.set('ref', ref);
      destinationUrl.search = searchParams.toString();
    }

    return { label: name, value: destinationUrl.toString(), ref };
  });

  return (
    <Box bgColor="baseNeutral.100">
      <Head title={
        metaPage && metaPage > 1
          ? t('sellPage:header.titleNextPages', { page: metaPage })
          : t('sellPage:header.title')
      }
      />
      <MetaTags meta={
        metaPage && metaPage > 1
          ? t(`sellPage:metaNextPages`, { returnObjects: true, page: metaPage })
          : t(`sellPage:meta`, { returnObjects: true, month: (new Date()).toLocaleDateString('it-IT', { month: 'long' }) })}
      />
      <LinkRel
        rel="canonical"
        href={`https://www.carplanner.com/offerte-auto${page && page > 1 ? `?pagina=${page}` : ''}`}
      />

      <PageHeaderContainer
        title={t('sellPage:header.title')}
        description={t('sellPage:header.description')}
        crumbs={crumbs}
        domain="sell"
        resultFormatter = {resultFormatter}
      />

      <Box w="100%" display="inline-block" pb="24px" pl="1.5rem">
        <ActionBar />

        <SellOffersContainer />
      </Box>

      {requestedPage < 2 &&
        <SellMoreInfoContainer />
      }

      {visibleOnMobile &&
        <MobileActionBar />
      }

    </Box>
  )
}

export const getServerSideProps = wrapper.getServerSideProps(store => async ({ query, res }) => {
  const props = await getSellPagesData(store, query)

  res.setHeader("Cache-Control", PAGE_CACHE_CONTROL);

  return { props: { ...props } }
})

export default SellSearchPage
